import { motion } from "framer-motion";
import { Mail, Phone, Linkedin, Instagram } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/site/Reveal";
import ContactForm from "@/components/site/ContactForm";
import { useLang } from "@/i18n";
import { CONSULT } from "@/i18n-consult";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_DISPLAY, LINKEDIN_URL, INSTAGRAM_URL } from "@/config";

const Contatti = () => {
  const { lang } = useLang();
  const c = CONSULT[lang];

  return (
    <div data-testid="contatti-page">
      <section className="bg-paper py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent"
          >
            <span className="h-px w-10 bg-accent" /> {c.hero.label}
          </motion.p>
          <h1 className="max-w-4xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl" data-testid="contatti-headline">
            <MaskedLine delay={0.1}>{c.hero.l1}</MaskedLine>
            <MaskedLine delay={0.25}><span className="italic text-accent">{c.hero.l2}</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-mutedink sm:text-lg"
          >
            {c.hero.sub}
          </motion.p>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-paper pb-24 lg:pb-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl font-light text-ink">{c.left.title}</h2>
              <p className="mt-5 text-base leading-relaxed text-mutedink">{c.left.body}</p>
            </Reveal>
            <div className="mt-10 divide-y divide-ink/10 border-y border-ink/10" data-testid="direct-contacts">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="group flex items-center gap-5 py-5 transition-colors hover:bg-white"
              >
                <Mail className="h-5 w-5 shrink-0 text-accent" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">{c.left.emailLabel}</p>
                  <p className="mt-1 text-sm font-medium text-ink transition-colors group-hover:text-accent">{CONTACT_EMAIL}</p>
                </div>
              </a>
              <a
                href={`tel:${CONTACT_PHONE}`}
                data-testid="contact-phone-link"
                className="group flex items-center gap-5 py-5 transition-colors hover:bg-white"
              >
                <Phone className="h-5 w-5 shrink-0 text-accent" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">{c.left.phoneLabel}</p>
                  <p className="mt-1 text-sm font-medium text-ink transition-colors group-hover:text-accent">{CONTACT_PHONE_DISPLAY}</p>
                </div>
              </a>
              {LINKEDIN_URL && (
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-link"
                  className="group flex items-center gap-5 py-5 transition-colors hover:bg-white"
                >
                  <Linkedin className="h-5 w-5 shrink-0 text-accent" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">{c.left.linkedinLabel}</p>
                    <p className="mt-1 text-sm font-medium text-ink transition-colors group-hover:text-accent">Cristina Angelini</p>
                  </div>
                </a>
              )}
              {INSTAGRAM_URL && (
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="group flex items-center gap-5 py-5 transition-colors hover:bg-white"
                >
                  <Instagram className="h-5 w-5 shrink-0 text-accent" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">Instagram</p>
                    <p className="mt-1 text-sm font-medium text-ink transition-colors group-hover:text-accent">@marketingfacile_cristina</p>
                  </div>
                </a>
              )}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <ContactForm />
          </motion.div>
        </div>

        <div className="mx-auto mt-24 max-w-7xl px-6 sm:px-8 lg:px-12" data-testid="how-it-works">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{c.how.overline}</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-light tracking-tight text-ink sm:text-4xl">{c.how.title}</h2>
          </Reveal>
          <div className="mt-12 grid gap-px bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {c.how.steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08} className="bg-paper p-8 transition-colors duration-300 hover:bg-white" >
                <p className="font-serif text-5xl font-light text-outline">{s.n}</p>
                <h3 className="mt-5 font-serif text-xl font-normal text-ink" data-testid={`how-step-${i}`}>{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mutedink">{s.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contatti;
